/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { User } from "@auth0/auth0-react";
import { stringSortComparatorV1 } from "@startree-ui/platform-ui";
import i18next from "i18next";
import { clone, get, isEmpty, kebabCase } from "lodash";
import {
    CloudVersion,
    OrganizationRole,
    PlatformRole,
    PlatformUser,
} from "../../rest/dto/user.interfaces";

export const STARTREE_DOMAIN = "startree.ai";

export const TRIALS_EMAIL = "trials@startree.ai";

const cloudPortalV1toV2BaseUrlMapping: Record<string, string> = {
    "https://startree.cloud": "https://cloud-portal.startree.cloud",
    "https://startree-staging.cloud":
        "https://cloud-portal.startree-staging.cloud",
    "https://startree-dev.cloud": "https://cloud-portal.startree-dev.cloud",
    "http://localhost:7006": "https://cloud-portal.startree-dev.cloud",
};

export const formatRoles = (roles: OrganizationRole[]): string => {
    if (isEmpty(roles)) {
        return "";
    }

    const roleStrings = [];
    const sortedRoles = clone(roles).sort(stringSortComparatorV1);
    for (const eachRole of sortedRoles) {
        roleStrings.push(i18next.t(`label.${kebabCase(eachRole)}`));
    }

    return roleStrings.join(" ");
};

export const isUserInternalAdmin = (user: PlatformUser): boolean => {
    if (isEmpty(user.roles) || !user.email) {
        return false;
    }

    if (
        user.roles.includes(PlatformRole.ROLE_admin) &&
        isStarTreeUser(user.email)
    ) {
        return true;
    }

    return false;
};

export const isSaasUser = (user: PlatformUser): boolean => {
    if (user.roles && user.roles.includes(PlatformRole.ROLE_saas_users)) {
        return true;
    }

    return false;
};

export const isStarTreeUser = (email: string): boolean => {
    if (email && email.split("@")[1] === STARTREE_DOMAIN) {
        return true;
    }

    return false;
};

export const isCloudV2User = (user?: User): boolean => {
    const cloudVersion =
        get(user, "cloudVersion") ||
        get(user, "https://startree.cloud/cloudVersion") ||
        "";

    return cloudVersion === CloudVersion.V2;
};

export const getCloudPortalV2BaseUrl = (): string => {
    const cloudPortalV1BaseUrl = window.location.origin;

    return (
        cloudPortalV1toV2BaseUrlMapping[cloudPortalV1BaseUrl] ||
        cloudPortalV1toV2BaseUrlMapping["https://startree.cloud"]
    );
};

export const getAppsUrlFromDmUrl = (
    dmUrl: string | undefined
): { clientsUrl: string; qcUrl: string; dpUrl: string } => {
    if (!dmUrl) {
        return {
            clientsUrl: "",
            qcUrl: "",
            dpUrl: "",
        };
    }

    const dpUrl = dmUrl.replace("://data.", "://dp.");
    const qcUrl = dpUrl + "/query-console";

    // TODO - move clients page to DP in future,
    // currently clients page in Data Portal can only be used by system-admins
    const appsUrl = dmUrl.replace("://data.", "://apps.");
    const clientsUrl = appsUrl + "/clients";

    return {
        clientsUrl,
        qcUrl,
        dpUrl,
    };
};
